import React, { Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { MockService, SV, TRANSACTIONAL } from 'types/rest/mockServiceTypes';
import { BlazemeterCredentials } from 'types/rest/blazeMeterApiResponse';
import MockServicesListBlazeTable from './MockServicesListBlazeTable';
import i18n from 'i18n';
import { confirm } from 'components/ui/modal/BlazeConfirmationModal';
import _ from 'lodash';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import MixpanelService from 'utils/mixpanelService';
import { CREATE_MOCK_SERVICE } from 'utils/mixpanelEvents';
import { isUpgradeMockWarningDisplay } from 'utils/mockServiceUtils';
import DropdownButton from 'components/ui/dropdown/DropdownButton';
import { Button } from 'react-bootstrap';
import BzmIcon from 'components/icons/BzmIcon';
import styles from './MockServices.module.css';
import ElevioTooltip from 'components/overlays/helpTooltip/TitleHelpTooltip/ElevioTooltip';
import { ApplicationState } from 'store/rootReducer';
import { createMockServiceThunk, updateMockServiceFilter } from 'store/mockServices/mockServiceActions';
import {
  bulkDeleteAction,
  getMockServiceListThunk,
  performBulkAction,
} from 'store/mockServices/mockServiceList/mockServiceListActions';
import { getMockServiceTemplates } from 'store/mockServiceTemplates/mockServiceTemplatesActions';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ShowUpgradableMocks } from 'store/ui/backdrop/backdropActions';
import { connect } from 'react-redux';
import FilterBar from 'views/MockServices/FilterBar/FilterBar';
import { getSelectedCountTitle } from '../../../../utils/miscUtil';
import { BlazeDropdownItem } from '../../../../components/ui/dropdown/DropdownButton/DropdownButton';
import { isFeatureEnabled } from '../../../../utils/bzmUserConfigUtil';

type MockServicesProps = StateProps & DispatchProps & WithTranslation;

interface LocalState {
  selectedMocks: MockService[];
  selectedCredentials: BlazemeterCredentials;
  currentAction: any;
  showWizardModal: boolean;
  showUpgradeWarnings: boolean;
  tabMsState: string;
}

export type MockServiceBulkAction = 'START' | 'STOP' | null;
class MockServices extends Component<MockServicesProps, LocalState> {
  // @ts-ignore
  msListTableRef: any = React.createRef<MockServicesListBlazeTable>();
  state = {
    selectedMocks: [] as MockService[],
    selectedCredentials: {} as BlazemeterCredentials,
    currentAction: null,
    showWizardModal: false,
    showUpgradeWarnings: false,
    tabMsState: 'mockservice',
  };

  onSelectionChange = (mocks: MockService[]) => {
    this.setState({ selectedMocks: mocks });
  };

  createDropdownItems = () => {
    const resultItems: BlazeDropdownItem[] = [];
    resultItems.push({
      content: this.props.t('mockServicesTable.createNewFromTxns'),
      fnCallback: () => {
        this.props.createMockService(TRANSACTIONAL);
      },
    });
    resultItems.push({
      content: this.props.t('mockServicesTable.startRecording'),
      fnCallback: this.redirectToBzRecorder,
    });
    return resultItems;
  };

  /* Show all mocks or only upgradable mocks */
  showOnlyWarmingMocks = () => {
    this.props.ShowUpgradableMocks();
    this.props.getMockServiceListThunk();
  };

  showUpgradeWarnings = (mocks: MockService[]) => {
    if (!this.state.showUpgradeWarnings) {
      for (const mockService of mocks) {
        if (isUpgradeMockWarningDisplay(mockService)) {
          this.setState({ showUpgradeWarnings: true });
          break;
        }
      }
    } else {
      let hasUpgradableMocks = false;
      for (const mockService of mocks) {
        if (isUpgradeMockWarningDisplay(mockService)) {
          hasUpgradableMocks = true;
        }
      }
      if (!hasUpgradableMocks) {
        this.setState({ showUpgradeWarnings: false });
      }
    }
  };

  onBulkDeleteClicked = () => {
    const modalBody = (
      <p>
        {i18n.t('marFiles:table.bulkDeleteWarn')}{' '}
        <strong className="text-break">{this.state.selectedMocks.length}</strong>{' '}
        {this.state.selectedMocks.length === 1
          ? ` ${i18n.t('mockServices:mockServicesTable.bulkDeleteSingleMock')}`
          : ` ${i18n.t('mockServices:mockServicesTable.bulkDeleteMultipleMock')}`}
        <br />
        {i18n.t('mockServices:mockServicesTable.cannotUndo')}
      </p>
    );
    confirm({
      confirmation: modalBody,
      yesOptionTitle: `${i18n.t('common:actions.delete')}`,
      title: `${i18n.t('mockServices:mockServicesTable.deleteSelectedMs')}`,
    })
      .then(() => {
        this.onBulkDeleteConfirmed();
      })
      .catch(() => {
        //User pressed No
      });
  };

  componentDidMount() {
    this.props.getMockServiceListThunk();
    this.showUpgradeWarnings(this.props.mockServicesList);
  }

  componentDidUpdate(prevProps: Readonly<MockServicesProps>) {
    if (prevProps.mockServicesList !== this.props.mockServicesList) {
      this.setState({ selectedMocks: [] });
      this.showUpgradeWarnings(this.props.mockServicesList);
    }
  }

  disableBulkStop = () => {
    const selectedMocks = this.state.selectedMocks;
    return (
      selectedMocks.length === 0 ||
      selectedMocks.filter(mock => mock.status === 'RUNNING' && mock.metaData.inProgress === false).length === 0
    );
  };

  disableBulkDelete = () => {
    return this.state.selectedMocks.length === 0;
  };

  disableBulkStart = () => {
    const selectedMocks = this.state.selectedMocks;
    let containsSvMock = false;

    // Disable if all selected are either in progress or running and also disable
    // if there is at least one SV mock in the selection
    const isSelectedInProgress =
      selectedMocks.length === 0 ||
      selectedMocks.filter(mock => {
        if (mock.type === 'SV') {
          containsSvMock = true;
        }
        return mock.status === 'RUNNING' || mock.status === 'CONFIGURING' || mock.metaData.inProgress === true;
      }).length === selectedMocks.length;

    return isSelectedInProgress || containsSvMock;
  };

  locationAbsent = (mock: MockService) => {
    if (mock.type === TRANSACTIONAL && (_.isEmpty(mock.harborId) || _.isEmpty(mock.shipId))) {
      return true;
    }
    if (
      mock.type === SV &&
      _.isEmpty(mock.harborId) &&
      _.isEmpty(mock.shipId) &&
      _.isEmpty(mock.bridgeId) &&
      _.isEmpty(mock.registryId) &&
      _.isEmpty(mock.vseId)
    ) {
      return true;
    }
    return false;
  };

  unSelectMockServices = () => {
    if (this.msListTableRef) {
      this.msListTableRef.deSelectAll();
    }
  };

  onBulkActionClicked = (action: MockServiceBulkAction) => {
    const mockNamesWithoutLocation: string[] = [];
    const mocksWithUpdatedState: MockService[] = [];

    let filteredMs: MockService[];
    if (action === 'START') {
      filteredMs = this.state.selectedMocks.filter(
        fm =>
          (fm.status === undefined || fm.status === 'STOPPED' || fm.status === 'FAILED') &&
          fm.metaData.inProgress === false,
      );
    } else {
      filteredMs = this.state.selectedMocks.filter(fm => fm.status === 'RUNNING' && fm.metaData.inProgress === false);
    }

    filteredMs.forEach(selectedMock => {
      const mock = this.props.mockServicesList.find(mock => mock.id === selectedMock.id);
      if (mock !== undefined) {
        if (this.locationAbsent(mock)) {
          mockNamesWithoutLocation.push(mock.name);
        }
        mocksWithUpdatedState.push(mock);
      }
    });

    if (mockNamesWithoutLocation.length > 0) {
      this.props.showToastNotification(
        ToastType.Danger,
        `${i18n.t('mockServices:errorMsgs.noLocation')}`,
        mockNamesWithoutLocation.join(','),
      );
    }

    if (mockNamesWithoutLocation.length > 0) {
      this.unSelectMockServices();
      return;
    }

    this.props.performBulkAction(mocksWithUpdatedState, action, this.unSelectMockServices);
  };

  onBulkDeleteConfirmed = () => {
    this.props.bulkDeleteAction(this.state.selectedMocks);
  };

  redirectToBzRecorder = () => {
    if (this.props.blazeUserConfig.blazeUserConfig != null) {
      window.open(
        this.props.blazeUserConfig.blazeUserConfig.config.blazeMeterAppUrl + 'recorder?force_record_response=true',
        '_blank',
      );
      MixpanelService.track(CREATE_MOCK_SERVICE('RECORDER'), true);
    }
  };

  render() {
    const { t } = this.props;
    this.showUpgradeWarnings(this.props.mockServicesList);
    const headerText = getSelectedCountTitle(this.props.mockServiceListCount, this.state.selectedMocks.length);

    const finalMsg = t('mockServicesUpgradeAlert.mocksUpgradeAlert');
    const buttonLabel = this.props.showUpgradableMockOnly
      ? t('mockServicesUpgradeAlert.showAllTitle')
      : t('mockServicesUpgradeAlert.showOnlyUpgradableMocks');
    const upgradeMockFilterButtonCss = this.props.showUpgradableMockOnly ? 'active' : '';

    return (
      <div className={'card'}>
        <div className={'card-body'}>
          <FilterBar
            className={'mb-4'}
            isGlobalServiceFilter={false}
            config={{
              locationFilter: true,
              searchFilter: true,
              serviceFilter: true,
              statusFilter: true,
              typeFilter: true,
            }}
            filter={this.props.filter}
            searchContext={'mockServices'}
            onChange={(filter, source) => {
              this.props.updateMockServiceFilter(filter);
              if (source !== 'search' && source !== 'type') {
                this.props.getMockServiceListThunk();
              }
            }}
            disableAddService={false}
            isCloneEnabled={true}
          />

          <div className={'float-right d-inline'}>
            <Button
              className={'btn btn-primary btn-circular btn-jb'}
              disabled={this.props.isPrivateCloudReadOnly}
              onClick={() => this.props.createMockService(TRANSACTIONAL)}
              title={t('mockServicesTable.createNew')}
            >
              <BzmIcon variant="icon-bzm-add" className="icon-bzm-add" />
            </Button>
          </div>

          <Button
            className={'float-right mr-4'}
            variant="secondary"
            style={{ marginTop: '11px' }}
            disabled={false}
            onClick={() => this.props.getMockServiceListThunk()}
          >
            <BzmIcon variant="icon-bzm-sync" />
            <span className="ml-2">{t('mockServicesTable.refresh')}</span>
          </Button>
          <div id="serviceMockTitle" className={` mb-4`}>
            <h2 className={styles.title + ' mb-2'}>
              {t('mockServicesTable.title')} {headerText}
              <ElevioTooltip elevioId={'MockServices_heading_help'} id={'mockServicesHelpId'} />
              <button
                id={`qa-blk-ms-play`}
                className="btn btn-link btn-no-hover p-0 mx-2"
                title={t('mockServicesTable.runSelectedMs')}
                onClick={() => this.onBulkActionClicked('START')}
                disabled={this.disableBulkStart()}
              >
                <BzmIcon variant={'icon-bzm-play-circle'} className={'icon-sm'} />
              </button>
              <button
                id={`qa-blk-ms-stop`}
                className="btn btn-link btn-no-hover p-0 mx-2"
                title={t('mockServicesTable.stopSelectedMs')}
                onClick={() => this.onBulkActionClicked('STOP')}
                disabled={this.disableBulkStop()}
              >
                <BzmIcon variant={'icon-bzm-stop-circle'} className={'icon-sm'} />
              </button>
              <button
                id={`qa-blk-ms-delete`}
                className="btn btn-link btn-no-hover p-0 mx-2"
                title={t('mockServicesTable.deleteSelectedMs')}
                onClick={this.onBulkDeleteClicked}
                disabled={this.disableBulkDelete()}
              >
                <BzmIcon variant={'icon-bzm-delete'} className={'icon-sm'} />
              </button>
            </h2>
            <p id="serviceMockVerbiage"> {t('mockServicesTable.description')} </p>
            {this.state.showUpgradeWarnings && (
              <div className={'alert alert-warning'}>
                <BzmIcon variant={'icon-bzm-caution'} className={'alert-icon'} />
                <button
                  className={'float-right btn btn-warning ' + upgradeMockFilterButtonCss}
                  style={{ marginTop: '-5px' }}
                  onClick={this.showOnlyWarmingMocks}
                >
                  <BzmIcon variant={'icon-bzm-filter'} style={{ paddingRight: '10px' }} />
                  {buttonLabel}
                </button>
                <p className={'alert-message mb-0'} dangerouslySetInnerHTML={{ __html: finalMsg }} />
              </div>
            )}
          </div>
          <MockServicesListBlazeTable
            onSelectionChange={this.onSelectionChange}
            onRef={(ref: any) => (this.msListTableRef = ref)}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: ApplicationState) => ({
  mockServicesList: state.mockservices.dataList,
  mockServiceListCount: state.mockservices.msListCount,
  blazeUserConfig: state.blazeUserConfig,
  isPrivateCloudReadOnly: state.blazeUserConfig.isPrivateCloudReadOnly,
  showUpgradableMockOnly: state.ui.showUpgradableMockOnly,
  selectCredsModalOpen: false,
  filter: state.mockservices.filter,
});

const mapDispatchToProps = {
  createMockService: createMockServiceThunk,
  getMockServiceListThunk,
  getMockServiceTemplates,
  bulkDeleteAction,
  performBulkAction,
  showToastNotification,
  ShowUpgradableMocks,
  updateMockServiceFilter,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withTranslation('mockServices')(connect(mapStateToProps, mapDispatchToProps)(MockServices));
